import {
    ColumnDataType,
    IBackendResult,
    ITableResult,
    ITableResultMeta,
    TableMetaDescriptor,
    ValueDescriptor,
} from "@inode/types2";
import { QueryType } from "store/tables";
import {
    ColumnDescriptor,
    ColumnValueDescription,
    // MetadataTable,
    // TableDataMeta,
} from "@inode/pilot-client-typescript-fetch";
import { isPresent, Optional } from "#types";

export type DataCell = IBackendResult;

export type DataRow = DataCell[];

export type Table = {
    title: string;
    type: "table";
    columns: string[];
    data: DataRow[];
    meta?: ITableResultMeta;
};

/**
 * @deprecated
 */
type SystemResponse = {
    // The id of the system
    sysId: string;
    totalTime: number;

    // The SQL queries produced by the system
    interpretations: Interpretation[];

    // The time taken by every component of the system.
    componentsTime: {
        componentName: string;
        time: number;
    }[];

    // The statistics returned by every component of the system.
    componentStats: {
        componentName: string;
        componentInfo: {
            title: string;
            columnTitles: string[];
            rows: {
                columnValues: string[];
            }[];
        }[];
    }[];
};

/**
 * Response is a parametrized class storing information about the :
 * 1. payload of the backend response
 * 2. any error that might happen with that request.
 */
/**
 * @deprecated
 */
interface PilotResponseContainer<T> {
    error: number; // 0 if none 1 if error exists
    errorMsg: string;
    payload: T; // The payload of the response
}

/**
 * @deprecated
 */
type Interpretation = {
    sql: string;
    network: string[];
    tableId: string;
};

export class IQueryTableResult implements ITableResult {
    public readonly columns: string[];
    public readonly data: DataRow[];
    public readonly type: "table" = "table";
    public readonly sourceQuery: string;
    public readonly queryType: QueryType = QueryType.SQL;
    public readonly meta: ITableResultMeta;
    public readonly tableId: number;
    public readonly interpretationId?: number;
    public readonly caption?: string;

    constructor(
        { columns, data, metadata }: any,
        tableId: number,
        uid?: number
    ) {
        this.columns = columns || [];
        this.data = data as DataRow[];
        this.sourceQuery = metadata.sourceQuery ?? "no source query available";
        this.caption = metadata.caption;
        this.meta = metadata2Meta(metadata || {}, uid);
        this.tableId = tableId;
        this.interpretationId = uid;
    }
}

const metadata2Meta = (
    {
        columns,
        classes,
        dateformats,
        relevancies,
        descriptors,
        redundancyMatrix,
        relation,
        types,
        labels,
        prefixSimilarityMatrix,
        ...rest
    }: any,
    uid?: number
): ITableResultMeta => {
    return {
        interpretationId: uid,
        ...rest,
        descriptors:
            descriptors
                ?.filter(isPresent)
                .map((x: ColumnDescriptor) =>
                    mapColumnDescriptorToTableMetaDescriptor(x)
                ) || [],
        types: types as ColumnDataType[],
        columns: columns,
        classes: classes.map((x: any) => null),
        labels: labels,
        dateformats: dateformats as unknown[],
        name: relation as unknown as string,
        relevancies: relevancies,
        prefixSimilarityMatrix: prefixSimilarityMatrix,
        redundancyMatrix: redundancyMatrix,
    };
};

function mapValueDescriptor(
    columnValueDescription: Optional<ColumnValueDescription>
): ValueDescriptor {
    if (!columnValueDescription)
        return {
            histogram: [],
            "@type": "categorical",
            unique: 0,
            nulls: 0,
        };

    const { type, ...rest } = columnValueDescription;
    return {
        "@type": type,
        ...rest,
    } as unknown as ValueDescriptor;
}

function mapColumnDescriptorToTableMetaDescriptor(
    descriptor: ColumnDescriptor
): TableMetaDescriptor {
    return {
        majorityType: descriptor.majorityType as ColumnDataType,
        valueDescription: mapValueDescriptor(descriptor.valueDescription),
        majorityTypeFraction: descriptor.majorityTypeFraction,
        typeDistribution: descriptor.typeDistribution,
    };
}
